/**
 * Phase 184 — Comptabilité / État de Cut-off
 *
 * Génère un état comptable de coupure pour la passation au comptable :
 * compare l'avancement HT d'une commande avec le total HT des factures
 * (fallback : paiements convertis en HT via vat_rate).
 */
import { useState, useMemo, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format as fmtDate, parseISO, isValid as isValidDate } from "date-fns";
import { fr } from "date-fns/locale";
import { Calendar as CalIcon, Download, FileSpreadsheet, FileText, ArrowUp, ArrowDown, ArrowUpDown, GripVertical, Eye, EyeOff, Loader2, AlertCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  horizontalListSortingStrategy,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { getHeaders } from "@/lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLUMNS = [
  { id: "client_name",      label: "Client",         align: "left",  type: "string" },
  { id: "client_city",      label: "Ville",          align: "left",  type: "string" },
  { id: "order_reference",  label: "N° commande",    align: "left",  type: "string" },
  { id: "order_date",       label: "Date commande",  align: "left",  type: "date"   },
  { id: "order_type_name",  label: "Type de commande", align: "left", type: "string" },
  { id: "amount_ht",        label: "Total HT (€)",   align: "right", type: "money"  },
  { id: "vat_rate",         label: "TVA (%)",        align: "right", type: "percent"},
  { id: "progress_percent", label: "Avancement (%)", align: "right", type: "percent"},
  { id: "advancement_ht",   label: "Avancement HT (€)", align: "right", type: "money" },
  { id: "invoices_ht",      label: "Factures HT (€)", align: "right", type: "money" },
  { id: "cutoff_type",      label: "Cut-off",        align: "center", type: "string" },
  { id: "cutoff_amount",    label: "Montant cut-off (€)", align: "right", type: "money" },
];

const fmtMoney = (v) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(Number(v || 0));
const fmtPct = (v) => `${Number(v || 0).toFixed(2)} %`;
const fmtIsoDate = (s) => {
  if (!s) return "—";
  const d = parseISO(s);
  return isValidDate(d) ? fmtDate(d, "dd/MM/yyyy", { locale: fr }) : s;
};

function renderCell(row, col) {
  const v = row[col.id];
  switch (col.type) {
    case "money":   return fmtMoney(v);
    case "percent": return fmtPct(v);
    case "date":    return fmtIsoDate(v);
    default:        return v ?? "—";
  }
}

// --------- Sortable Header Cell ----------
const SortableHeader = ({ col, sortKey, sortDir, onSort }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: col.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const isSorted = sortKey === col.id;
  return (
    <th
      ref={setNodeRef}
      style={style}
      className={`text-${col.align} text-xs font-semibold uppercase tracking-wide text-muted-foreground bg-slate-100 border-b px-2 py-2 select-none whitespace-nowrap`}
      data-testid={`cutoff-th-${col.id}`}
    >
      <div className="inline-flex items-center gap-1.5">
        <button
          type="button"
          className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground touch-none"
          aria-label="Glisser pour réordonner"
          data-testid={`cutoff-th-drag-${col.id}`}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={() => onSort(col.id)}
          className="hover:text-foreground inline-flex items-center gap-1"
          data-testid={`cutoff-th-sort-${col.id}`}
        >
          <span>{col.label}</span>
          {isSorted
            ? (sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)
            : <ArrowUpDown className="h-3 w-3 opacity-40" />}
        </button>
      </div>
    </th>
  );
};

// --------- Sortable visibility row (settings popover) ----------
const VisibilityRow = ({ col, visible, onToggle }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: col.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted/50"
      data-testid={`cutoff-vis-row-${col.id}`}
    >
      <button
        type="button"
        className="cursor-grab active:cursor-grabbing text-muted-foreground touch-none p-0.5"
        {...attributes}
        {...listeners}
        aria-label="Glisser"
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <Checkbox
        checked={visible}
        onCheckedChange={() => onToggle(col.id)}
        data-testid={`cutoff-vis-toggle-${col.id}`}
      />
      {visible
        ? <Eye className="h-3.5 w-3.5 text-muted-foreground" />
        : <EyeOff className="h-3.5 w-3.5 text-muted-foreground" />}
      <span className={`text-sm flex-1 ${visible ? "" : "text-muted-foreground line-through"}`}>{col.label}</span>
    </div>
  );
};

// --------- Main page ----------
export default function AccountingCutOffPage() {
  const [situationDate, setSituationDate] = useState(null);
  const [periodStart, setPeriodStart] = useState(null);
  const [periodEnd, setPeriodEnd] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(null); // "xlsx" | "pdf"
  const [data, setData] = useState(null);

  // Column order + visibility (persisted to localStorage)
  const [colOrder, setColOrder] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("cutoff_col_order") || "null");
      if (Array.isArray(saved) && saved.length > 0) {
        // Merge: keep saved order, then append any new columns (e.g. order_type_name)
        const known = new Set(COLUMNS.map((c) => c.id));
        const kept = saved.filter((id) => known.has(id));
        const missing = COLUMNS.map((c) => c.id).filter((id) => !kept.includes(id));
        return [...kept, ...missing];
      }
    } catch { /* ignore */ }
    return COLUMNS.map((c) => c.id);
  });
  const [hidden, setHidden] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem("cutoff_hidden") || "[]"));
    } catch { return new Set(); }
  });
  const [sortKey, setSortKey] = useState("order_date");
  const [sortDir, setSortDir] = useState("desc");

  const persistOrder = (order) => {
    setColOrder(order);
    localStorage.setItem("cutoff_col_order", JSON.stringify(order));
  };
  const persistHidden = (set) => {
    setHidden(new Set(set));
    localStorage.setItem("cutoff_hidden", JSON.stringify([...set]));
  };

  const orderedCols = useMemo(
    () => colOrder.map((id) => COLUMNS.find((c) => c.id === id)).filter(Boolean),
    [colOrder]
  );
  const visibleCols = useMemo(
    () => orderedCols.filter((c) => !hidden.has(c.id)),
    [orderedCols, hidden]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEndCols = useCallback((event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = colOrder.indexOf(active.id);
    const newIdx = colOrder.indexOf(over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    persistOrder(arrayMove(colOrder, oldIdx, newIdx));
  }, [colOrder]);

  const toggleVisible = useCallback((id) => {
    const next = new Set(hidden);
    if (next.has(id)) next.delete(id); else next.add(id);
    persistHidden(next);
  }, [hidden]);

  const onSort = (key) => {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const sortedRows = useMemo(() => {
    if (!data?.rows) return [];
    const arr = [...data.rows];
    arr.sort((a, b) => {
      const va = a[sortKey];
      const vb = b[sortKey];
      // null/undefined → push to end
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === "number" && typeof vb === "number") {
        return sortDir === "asc" ? va - vb : vb - va;
      }
      return sortDir === "asc"
        ? String(va).localeCompare(String(vb), "fr")
        : String(vb).localeCompare(String(va), "fr");
    });
    return arr;
  }, [data, sortKey, sortDir]);

  // ---- Build query params ----
  // Le sélecteur de date unique et la période s'auto-excluent côté UI
  // (l'un efface l'autre dans onSelect). On garde toutefois le contrôle
  // strict ici en cas de saisie programmatique.
  const buildParams = () => {
    const usesSituation = !!situationDate;
    const usesPeriod = !!periodStart && !!periodEnd;
    if (!usesSituation && !usesPeriod) {
      toast.error("Renseignez une date de situation OU une période complète.");
      return null;
    }
    if (usesSituation) {
      return { situation_date: fmtDate(situationDate, "yyyy-MM-dd") };
    }
    if (periodStart > periodEnd) {
      toast.error("La date de début doit être ≤ à la date de fin.");
      return null;
    }
    return {
      period_start: fmtDate(periodStart, "yyyy-MM-dd"),
      period_end: fmtDate(periodEnd, "yyyy-MM-dd"),
    };
  };

  const generate = async () => {
    const params = buildParams();
    if (!params) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/accounting/cut-off`, { headers: getHeaders(), params });
      setData(res.data);
      if (res.data.count_rows === 0) {
        toast.info("Aucune commande dans le périmètre choisi.");
      } else {
        toast.success(`Cut-off généré — ${res.data.count_rows} commande(s)`);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Erreur lors de la génération";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const exportFile = async (format) => {
    const params = buildParams();
    if (!params) return;
    setExporting(format);
    try {
      const res = await axios.get(`${API}/accounting/cut-off/export`, {
        headers: getHeaders(),
        params: { ...params, format },
        responseType: "blob",
      });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/i);
      const filename = m ? m[1] : `cut-off.${format}`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Export ${format.toUpperCase()} téléchargé`);
    } catch (e) {
      toast.error(`Erreur export ${format.toUpperCase()}`);
    } finally {
      setExporting(null);
    }
  };

  // ---- Render ----
  const subtotalsEntries = data
    ? Object.entries(data.subtotals_by_vat_and_type).sort((a, b) => Number(a[0]) - Number(b[0]))
    : [];

  return (
    <TooltipProvider delayDuration={300}>
      <div className="space-y-5" data-testid="accounting-cutoff-page">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Comptabilité — Cut-off</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Comparaison de l&apos;avancement HT et du total HT facturé pour identifier les <b>PCA</b> (Produits Constatés d&apos;Avance)
            et les <b>FAE</b> (Factures À Établir) à la date choisie.
          </p>
        </div>

        {/* Filters */}
        <Card data-testid="cutoff-filters-card">
          <CardHeader>
            <CardTitle className="text-lg">Filtres de sélection</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div>
                <Label className="text-xs">Date de situation</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start mt-1" data-testid="cutoff-situation-trigger">
                      <CalIcon className="h-4 w-4 mr-2" />
                      {situationDate ? fmtDate(situationDate, "PPP", { locale: fr }) : "Choisir une date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={situationDate}
                      onSelect={(d) => { setSituationDate(d || null); if (d) { setPeriodStart(null); setPeriodEnd(null); } }}
                      locale={fr}
                    />
                  </PopoverContent>
                </Popover>
                {situationDate && (
                  <button
                    type="button"
                    onClick={() => setSituationDate(null)}
                    className="text-xs text-muted-foreground hover:text-foreground mt-1"
                    data-testid="cutoff-situation-clear"
                  >
                    Effacer
                  </button>
                )}
              </div>

              <div className="md:col-span-2 grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Date de début (période)</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start mt-1" data-testid="cutoff-period-start-trigger">
                        <CalIcon className="h-4 w-4 mr-2" />
                        {periodStart ? fmtDate(periodStart, "PPP", { locale: fr }) : "Choisir une date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={periodStart}
                        onSelect={(d) => { setPeriodStart(d || null); if (d) setSituationDate(null); }}
                        locale={fr}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label className="text-xs">Date de fin (période)</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start mt-1" data-testid="cutoff-period-end-trigger">
                        <CalIcon className="h-4 w-4 mr-2" />
                        {periodEnd ? fmtDate(periodEnd, "PPP", { locale: fr }) : "Choisir une date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={periodEnd}
                        onSelect={(d) => { setPeriodEnd(d || null); if (d) setSituationDate(null); }}
                        locale={fr}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Button onClick={generate} disabled={loading} data-testid="cutoff-generate-btn">
                {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Générer le cut-off
              </Button>
              <Button
                variant="outline"
                onClick={() => exportFile("xlsx")}
                disabled={exporting !== null || !data}
                data-testid="cutoff-export-xlsx-btn"
              >
                {exporting === "xlsx"
                  ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  : <FileSpreadsheet className="h-4 w-4 mr-2" />}
                Excel
              </Button>
              <Button
                variant="outline"
                onClick={() => exportFile("pdf")}
                disabled={exporting !== null || !data}
                data-testid="cutoff-export-pdf-btn"
              >
                {exporting === "pdf"
                  ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  : <FileText className="h-4 w-4 mr-2" />}
                PDF
              </Button>

              <div className="ml-auto">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" size="sm" data-testid="cutoff-columns-btn">
                      <Eye className="h-4 w-4 mr-2" />
                      Colonnes
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-72 p-2">
                    <div className="text-xs font-medium text-muted-foreground px-2 pb-2">
                      Réordonner & afficher/masquer
                    </div>
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEndCols}>
                      <SortableContext items={colOrder} strategy={verticalListSortingStrategy}>
                        <div className="space-y-0.5">
                          {orderedCols.map((c) => (
                            <VisibilityRow
                              key={c.id}
                              col={c}
                              visible={!hidden.has(c.id)}
                              onToggle={toggleVisible}
                            />
                          ))}
                        </div>
                      </SortableContext>
                    </DndContext>
                    <div className="pt-2 mt-2 border-t flex justify-end">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          persistOrder(COLUMNS.map((c) => c.id));
                          persistHidden(new Set());
                        }}
                        data-testid="cutoff-columns-reset"
                      >
                        Réinitialiser
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {data && (
              <div className="mt-3 text-xs text-muted-foreground">
                {data.mode === "period"
                  ? <>Période du <b>{fmtIsoDate(data.period_start)}</b> au <b>{fmtIsoDate(data.period_end)}</b></>
                  : <>Situation au <b>{fmtIsoDate(data.cutoff_date)}</b></>}
                {" · "}
                {data.count_rows} commande(s)
              </div>
            )}
          </CardContent>
        </Card>

        {/* Table */}
        {data && (
          <Card data-testid="cutoff-results-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                Résultats
                <span className="text-xs font-normal text-muted-foreground">
                  ({sortedRows.length} ligne{sortedRows.length > 1 ? "s" : ""})
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto border-t" data-testid="cutoff-table-wrapper">
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEndCols}>
                  <SortableContext items={visibleCols.map((c) => c.id)} strategy={horizontalListSortingStrategy}>
                    <table className="w-full text-sm">
                      <thead>
                        <tr>
                          {visibleCols.map((col) => (
                            <SortableHeader
                              key={col.id}
                              col={col}
                              sortKey={sortKey}
                              sortDir={sortDir}
                              onSort={onSort}
                            />
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {sortedRows.length === 0 && (
                          <tr>
                            <td colSpan={visibleCols.length} className="text-center text-muted-foreground py-12">
                              <AlertCircle className="h-5 w-5 mx-auto mb-2 opacity-50" />
                              Aucune ligne à afficher
                            </td>
                          </tr>
                        )}
                        {sortedRows.map((row) => (
                          <tr
                            key={row.order_id}
                            className="border-b hover:bg-muted/30 transition-colors"
                            data-testid={`cutoff-row-${row.order_id}`}
                          >
                            {visibleCols.map((col) => (
                              <td key={col.id} className={`text-${col.align} px-2 py-1.5 align-top whitespace-nowrap`}>
                                {col.id === "cutoff_type" ? (
                                  row.cutoff_type ? (
                                    <Badge
                                      variant="outline"
                                      className={
                                        row.cutoff_type === "PCA"
                                          ? "border-amber-300 bg-amber-50 text-amber-800"
                                          : "border-purple-300 bg-purple-50 text-purple-800"
                                      }
                                      data-testid={`cutoff-type-${row.order_id}`}
                                    >
                                      {row.cutoff_type}
                                    </Badge>
                                  ) : (
                                    <span className="text-muted-foreground">—</span>
                                  )
                                ) : col.id === "invoices_ht" ? (
                                  <div className="flex flex-col items-end">
                                    <span>{fmtMoney(row.invoices_ht)}</span>
                                    {row.is_payment_fallback && (
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <span
                                            className="text-[10px] uppercase tracking-wide text-blue-700 bg-blue-50 border border-blue-200 rounded px-1 mt-0.5"
                                            data-testid={`cutoff-payment-tag-${row.order_id}`}
                                          >
                                            paiement
                                          </span>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          Pas de facture sur cette commande — montant calculé depuis les paiements (TTC) convertis en HT via le taux de TVA.
                                        </TooltipContent>
                                      </Tooltip>
                                    )}
                                  </div>
                                ) : (
                                  renderCell(row, col)
                                )}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </SortableContext>
                </DndContext>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Subtotals */}
        {data && data.count_rows > 0 && (
          <Card data-testid="cutoff-subtotals-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Sous-totaux & total général</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-100 border-b">
                      <th className="text-left px-3 py-2 text-xs font-semibold uppercase">Taux TVA</th>
                      <th className="text-right px-3 py-2 text-xs font-semibold uppercase">PCA (€)</th>
                      <th className="text-right px-3 py-2 text-xs font-semibold uppercase">FAE (€)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subtotalsEntries.length === 0 && (
                      <tr>
                        <td colSpan={3} className="text-center text-muted-foreground py-6">
                          Aucun cut-off à constater
                        </td>
                      </tr>
                    )}
                    {subtotalsEntries.map(([vat, vals]) => (
                      <tr key={vat} className="border-b" data-testid={`cutoff-subtotal-${vat}`}>
                        <td className="px-3 py-2">{Number(vat).toString()} %</td>
                        <td className="text-right px-3 py-2 text-amber-800 font-medium">{fmtMoney(vals.PCA)}</td>
                        <td className="text-right px-3 py-2 text-purple-800 font-medium">{fmtMoney(vals.FAE)}</td>
                      </tr>
                    ))}
                    <tr className="bg-slate-50 font-bold" data-testid="cutoff-total-row">
                      <td className="px-3 py-2">TOTAL</td>
                      <td className="text-right px-3 py-2 text-amber-800">{fmtMoney(data.totals_by_type.PCA)}</td>
                      <td className="text-right px-3 py-2 text-purple-800">{fmtMoney(data.totals_by_type.FAE)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="mt-3 text-sm">
                <span className="text-muted-foreground">Solde net (PCA - FAE) : </span>
                <b data-testid="cutoff-grand-total">{fmtMoney(data.grand_total_signed)}</b>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </TooltipProvider>
  );
}
